/* Header nav panel behavior:
   1. Caps desktop dropdowns at 88% of the viewport height (leaving a strip
      of page below to move the pointer onto to dismiss it); the link columns
      and product rail scroll independently inside it (see header CSS).
   2. Adds hover opening for fine-pointer devices. Click/tap behavior remains
      owned by AnimatedDetails in global.js. */
(() => {
  const PANEL_MAX_VIEWPORT_RATIO = 0.88;
  const header = document.getElementById('site-header');
  const getOpenPanel = () =>
    header?.querySelector('.site-header__inline-menu-item animated-details.is-open .dropdown-panel');

  const measurePanel = () => {
    if (!header) return;

    const viewport = window.visualViewport;
    const viewportHeight = viewport ? viewport.height : document.documentElement.clientHeight;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : viewportHeight;
    const headerBottom = header.getBoundingClientRect().bottom;
    const panelBottom = Math.min(viewportBottom, viewportHeight * PANEL_MAX_VIEWPORT_RATIO);
    const height = Math.max(0, panelBottom - headerBottom);

    document.documentElement.style.setProperty('--nav-panel-height', `${height}px`);
    updateScrollHints();

    // Align the panel's link column with the nav's first item, wherever
    // flex layout puts it (logo width, viewport size, and zoom all move it).
    const firstNavItem = header.querySelector('.site-header__inline-menu-item .site-header__link');
    if (firstNavItem) {
      const padLeft = firstNavItem.getBoundingClientRect().left;
      if (padLeft > 0) {
        document.documentElement.style.setProperty('--nav-panel-pad-left', `${padLeft}px`);
      }
    }
  };

  // Marks each scrollable column with which edges have more content beyond
  // them, so CSS can fade that edge (replaces the hidden scrollbar as the
  // "this scrolls" cue).
  const scrollColumns = header
    ? [...header.querySelectorAll('.dropdown-panel__navigation, .dropdown-panel__rail')]
    : [];

  const updateScrollHints = () => {
    scrollColumns.forEach((column) => {
      const canScrollUp = column.scrollTop > 2;
      const canScrollDown = column.scrollTop + column.clientHeight < column.scrollHeight - 2;
      column.toggleAttribute('data-fade-top', canScrollUp);
      column.toggleAttribute('data-fade-bottom', canScrollDown);
    });
  };

  scrollColumns.forEach((column) => {
    column.addEventListener('scroll', updateScrollHints, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(updateScrollHints).observe(column);
  });

  let measureFrame;
  const scheduleMeasurement = () => {
    window.cancelAnimationFrame(measureFrame);
    measureFrame = window.requestAnimationFrame(measurePanel);
  };

  const scheduleMeasurementWhenClosed = () => {
    if (!getOpenPanel()) scheduleMeasurement();
  };

  measurePanel();
  window.addEventListener('load', scheduleMeasurement);
  window.addEventListener('resize', scheduleMeasurement);
  window.addEventListener('scroll', scheduleMeasurementWhenClosed, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleMeasurement);
  window.visualViewport?.addEventListener('scroll', scheduleMeasurementWhenClosed);

  if (header && window.ResizeObserver) {
    new ResizeObserver(scheduleMeasurement).observe(header);
  }

  document.addEventListener('focusin', (event) => {
    if (event.target.closest('#site-header')) scheduleMeasurement();
  });

  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest('#site-header')) scheduleMeasurement();
  });

  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  customElements.whenDefined('animated-details').then(() => {
    document
      .querySelectorAll('.site-header__inline-menu-item')
      .forEach((item) => {
        const details = item.querySelector('animated-details');
        if (!details) return;

        let closeTimer;

        item.addEventListener('mouseenter', () => {
          clearTimeout(closeTimer);
          measurePanel();
          details.open();
          scheduleMeasurement();
        });

        item.addEventListener('mouseleave', () => {
          closeTimer = setTimeout(() => details.close(), 180);
        });
      });
  });
})();
